import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronRightIcon } from '@page/icons';

import { cn } from '../../lib/styles';
import { useAttributeValue } from '@page/core/hooks/dom/useAttributeValue';
import { useResizeObserver } from '@page/core/hooks/dom/useResizeObserver';

import { useSectionedAccordionLayout } from './useSectionedAccordionLayout';

export type AccordionVariant = 'default' | 'group' | 'section';

interface AccordionContextValue {
  variant: AccordionVariant;
  accordionHeight: number;
  naked?: boolean;
}

const AccordionContext = createContext<AccordionContextValue>({
  variant: 'default',
  accordionHeight: 0,
});

/** The bordered container look of a default (non-naked) accordion. */
const accordionContainerClassName = 'border border-border-default rounded-sm bg-gray-2 overflow-hidden';

function Accordion({
  className,
  naked = false,
  variant = 'default',
  ...rest
}: React.ComponentProps<typeof AccordionPrimitive.Root> & {
  naked?: boolean;
  variant?: AccordionVariant;
}) {
  const accordionRef = useRef<HTMLDivElement>(null);
  const [accordionHeight, setAccordionHeight] = useState<number>(0);

  useLayoutEffect(() => {
    if (variant !== 'group' || !accordionRef.current) return;

    const updateHeight = () => {
      setAccordionHeight(accordionRef.current?.offsetHeight || 0);
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(accordionRef.current);

    return () => resizeObserver.disconnect();
  }, [variant]);

  useSectionedAccordionLayout(accordionRef, variant === 'section');

  return (
    <AccordionContext.Provider value={{ variant, accordionHeight, naked }}>
      <AccordionPrimitive.Root
        ref={accordionRef}
        data-slot="accordion"
        {...(variant === 'group' && rest.type !== 'multiple' ? { type: 'single', collapsible: true } : {})}
        className={cn(
          'border-b-0',
          {
            [accordionContainerClassName]: !naked,
            'h-full flex flex-col': variant === 'group',
            'flex flex-1 flex-col overflow-hidden min-h-0': variant === 'section',
          },
          className,
        )}
        {...rest}
      />
    </AccordionContext.Provider>
  );
}

type AccordionItemContextValue = {
  isOpen: boolean;
};

const AccordionItemContext = createContext<AccordionItemContextValue>({
  isOpen: false,
});

export const useAccordionItemContext = () => useContext(AccordionItemContext);

export type AccordionItemProps = React.ComponentProps<typeof AccordionPrimitive.Item>;

function AccordionItem({ className, value, ...props }: AccordionItemProps) {
  const { variant } = useContext(AccordionContext);
  const ref = useRef<HTMLDivElement>(null);
  const stateValue = useAttributeValue(ref, 'data-state');

  const itemContext = useMemo(
    () => ({
      isOpen: stateValue === 'open',
    }),
    [stateValue],
  );

  return (
    <AccordionItemContext.Provider value={itemContext}>
      <AccordionPrimitive.Item
        data-slot="accordion-item"
        ref={ref}
        className={cn(
          {
            'data-[state=open]:flex-1 bg-bg-sur-primary': variant === 'group',
            'flex shrink-0 flex-col min-h-0': variant === 'section',
          },
          className,
        )}
        value={value}
        {...props}
      />
    </AccordionItemContext.Provider>
  );
}

interface AccordionTriggerStyleProps {
  containerClassName?: string;
  size?: 'sm' | 'md';
  /** Content rendered on the right side, just before the chevron (e.g. a count). */
  beforeChevron?: React.ReactNode;
}

const accordionTriggerContainerClassName = (size: 'sm' | 'md') =>
  cn(
    'cursor-pointer flex w-full gap-4 transition-colors outline-none',
    'focus-visible:ring-[1px] focus-visible:ring-blue-300 disabled:pointer-events-none disabled:opacity-50 [&[data-state=open]_svg[data-slot=accordion-trigger-icon]]:rotate-90',
    'disabled:**:data-[slot=accordion-trigger-icon]:opacity-0',
    'bg-gray-2 hover:bg-gray-5',
    { 'h-14': size === 'md', 'h-10': size === 'sm' },
  );

const AccordionTriggerBody = ({
  className,
  beforeChevron,
  chevronPosition = 'end',
  children,
}: {
  className?: string;
  beforeChevron?: React.ReactNode;
  chevronPosition?: 'start' | 'end';
  children?: React.ReactNode;
}) => {
  const chevron = (
    <div className="flex items-center justify-center size-6" data-slot="chevron">
      <ChevronRightIcon
        data-slot="accordion-trigger-icon"
        className="icon-primary pointer-events-none size-5 transition-transform duration-200"
      />
    </div>
  );

  return (
    <div
      className={cn(
        'pl-4 pr-2 flex flex-1 items-center justify-between gap-2',
        'text-left font-body-md capitalize font-semibold text-text-primary',
        className,
      )}
    >
      {chevronPosition === 'start' ? (
        <span className="flex min-w-0 items-center gap-1">
          {chevron}
          {children}
        </span>
      ) : (
        children
      )}

      <div className="flex shrink-0 items-center gap-2">
        <span className="font-body-sm font-normal text-text-tertiary">{beforeChevron}</span>

        {chevronPosition === 'end' ? chevron : null}
      </div>
    </div>
  );
};

function AccordionTrigger({
  className,
  children,
  containerClassName,
  headerClassName,
  size = 'md',
  beforeChevron,
  naked = false,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger> &
  AccordionTriggerStyleProps & {
    headerClassName?: string;
    /** No background/hover background, chevron rendered before the text. */
    naked?: boolean;
  }) {
  const { variant } = useContext(AccordionContext);

  return (
    <AccordionPrimitive.Header className={cn('flex', variant === 'section' && 'shrink-0', headerClassName)}>
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        className={cn(
          accordionTriggerContainerClassName(size),
          'flex-1',
          naked && 'bg-transparent hover:bg-transparent hover:opacity-80 transition-opacity',
          containerClassName,
        )}
        {...props}
      >
        <AccordionTriggerBody
          className={className}
          beforeChevron={beforeChevron}
          chevronPosition={naked ? 'start' : 'end'}
        >
          {children}
        </AccordionTriggerBody>
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
}

/**
 * A plain button styled like an `AccordionTrigger` inside a default accordion.
 */
function AccordionTriggerUI({
  className,
  children,
  containerClassName,
  size = 'md',
  beforeChevron,
  ...props
}: React.ComponentProps<'button'> & AccordionTriggerStyleProps) {
  return (
    <button
      type="button"
      data-slot="accordion-trigger-ui"
      className={cn(
        accordionTriggerContainerClassName(size),
        'shrink-0',
        accordionContainerClassName,
        containerClassName,
      )}
      {...props}
    >
      <AccordionTriggerBody className={className} beforeChevron={beforeChevron}>
        {children}
      </AccordionTriggerBody>
    </button>
  );
}

function AccordionContent({
  className,
  children,
  containerClassName,
  keepMounted = false,
  style,
  ...props
}: Omit<React.ComponentProps<typeof AccordionPrimitive.Content>, 'forceMount'> & {
  containerClassName?: string;
  /** Keeps closed content in the DOM, collapsed and inert, so in-page search can still find its text. */
  keepMounted?: boolean;
}) {
  const { naked, variant } = useContext(AccordionContext);
  const { isOpen } = useAccordionItemContext();
  const bodyRef = useRef<HTMLDivElement>(null);
  const [bodyHeight, setBodyHeight] = useState<number>();
  const handleBodyResize = useCallback((rect: DOMRectReadOnly) => setBodyHeight(rect.height), []);
  useResizeObserver(handleBodyResize, keepMounted ? bodyRef : undefined);

  // Radix measures a force-mounted content one toggle late, so the open/close animation reads the body's height instead.
  const contentStyle =
    keepMounted && bodyHeight !== undefined
      ? ({ ...style, '--radix-accordion-content-height': `${bodyHeight}px` } as React.CSSProperties)
      : style;

  return (
    <AccordionPrimitive.Content
      data-slot="accordion-content"
      forceMount={keepMounted || undefined}
      inert={keepMounted && !isOpen ? true : undefined}
      style={contentStyle}
      className={cn(
        'data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down',
        'overflow-hidden data-[state=open]:overflow-visible',
        'bg-bg-sur-primary',
        {
          'border border-border-default rounded-t-xl border-b-0 border-l-0 border-r-0': !naked,
          'group data-[state=open]:max-h-(--accordion-sectioned-open-max-h) data-[state=open]:min-h-0 data-[state=open]:overflow-hidden':
            variant === 'section',
          'data-[state=closed]:h-0 data-[state=closed]:border-t-0': keepMounted,
        },
        containerClassName,
      )}
      {...props}
    >
      <div
        ref={bodyRef}
        className={cn(
          'pb-4 px-2',
          {
            'pb-0 group-data-[state=open]:min-h-0 group-data-[state=open]:h-full group-data-[state=open]:overflow-x-hidden group-data-[state=open]:overflow-y-auto':
              variant === 'section',
          },
          className,
        )}
      >
        {children}
      </div>
    </AccordionPrimitive.Content>
  );
}

export { Accordion, AccordionItem, AccordionTrigger, AccordionTriggerUI, AccordionContent };
